/**
 *
 * Audiences
 *
 */

import React, { memo, useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Helmet } from "react-helmet";
import { createStructuredSelector } from "reselect";
import { compose } from "redux";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

// styles
import "./style.scss";

import { useInjectSaga } from "utils/injectSaga";
import { useInjectReducer } from "utils/injectReducer";
import reducer from "./reducer";
import saga from "./saga";

import {
  flushStateAudiences,
  // list
  loadAudiences,
} from "./actions";

import {
  makeSelectFirstRender,
  // list
  makeSelectItemsList,
  selectAudiencesLoading,
  selectAudiencesError,
  makeSelectItemsListFilteringParams,
  makeSelectItemsListTotalAmount,
  makeSelectItemsCurrentPage,
  makeSelectItemsTotalPages,
  makeSelectItemsLimitPerPage,
} from "./selectors";

// antd component
import { Layout, Button } from "antd";
const { Content } = Layout;
// components
import CustomTable from "components/CustomTable";
import CustomPageHeader from "components/CustomPageHeader";

// helper for forms
import { useRefState } from "utils/hooks";
import { parseQuery, encodeQueryData } from "utils/history";

export function Audiences({
  // main data
  itemsList,
  // states
  firstRender,
  history,
  // list
  error,
  loading,
  params,
  totalAmount,
  totalPages,
  limit,
  // actions
  loadList,
  flushState,
  //default
  className,
}) {
  useInjectReducer({ key: "Audiences", reducer });
  useInjectSaga({ key: "Audiences", saga });

  const { t } = useTranslation();

  const newKey = "new";
  const location = useLocation();
  const searchParams = parseQuery(location.search) || null;

  const [currentParams, currentParamsRef, setCurrentParams] = useRefState(
    searchParams || null,
  );

  const [tableRows, setTableRow] = useState([]);

  /**
   * Loading data for rendering in a component
   * - work like ComponentDidUpdate
   */
  useEffect(() => {
    if (!!!itemsList || itemsList?.length <= 0) {
      const initParams = {
        ...params,
        ...searchParams,
      };
      setCurrentParams(initParams);
      loadList(initParams, true);

      const { sortBy, ...searchData } = initParams;
      setSearchParams(searchData);
    }

    return () => {
      // Anything in here is fired on component unmount.
      // Clearing the state after unmounting a component
      flushState();
    };
  }, []);

  useEffect(() => {
    if (!!itemsList && itemsList?.length > 0) {
      const rows =
        itemsList?.length > 0 &&
        itemsList
          ?.map(elem => {
            return {
              ...elem,
              amount:
                Number(elem?.users?.length || 0) +
                Number(elem?.students?.length || 0),
              createdByName: elem?.createdBy?.description
                ? `${elem?.createdBy?.description?.firstname} ${
                    elem?.createdBy?.description?.lastname
                  }`
                : "",
            };
          })
          .sort((a, b) => a?.title.localeCompare(b?.title));
      setTableRow(rows);
    }
  }, [itemsList]);

  const setSearchParams = useCallback(
    data => {
      const search = parseQuery(location.search) || {};
      let currentData = {
        ...search,
        ...data,
      };
      const { sortBy, ...searchData } = currentData;
      const serachStr = encodeQueryData(searchData);
      history.push({
        search: `?${serachStr}`,
        state: {
          params: currentData,
        },
      });
    },
    [history, location.search],
  );

  // Change filter params and load new item for infinite scroll
  const changeFilterParams = useCallback(
    newParams => {
      if (
        newParams &&
        Object.keys(newParams).length > 0 &&
        currentParamsRef?.current &&
        Object.keys(currentParamsRef?.current).length > 0 &&
        JSON.stringify(currentParamsRef?.current) != JSON.stringify(newParams)
      ) {
        const newParamObj = {
          ...currentParamsRef?.current,
          ...newParams,
        };
        const { page, ...searchData } = newParamObj;
        setSearchParams(searchData);
        setCurrentParams(newParamObj);
        loadList(newParamObj, false);
      } else if (
        Object.keys(currentParamsRef?.current).length == 0 &&
        newParams &&
        Object.keys(newParams).length > 0
      ) {
        const newParamObj = { ...params, ...newParams };
        const { page, ...searchData } = newParamObj;
        setCurrentParams(newParamObj);
        setSearchParams(searchData);
        loadList(newParamObj, false);
      }
    },
    [params, currentParams, currentParamsRef],
  );

  const addNewItem = useCallback(() => {
    const search = parseQuery(location.search) || {};
    let currentData = search;
    const serachStr = encodeQueryData(currentData);
    history.push({
      pathname: `/audiences/${newKey}`,
      search: `?${serachStr}`,
      state: {
        params: currentData,
        from: "audiences",
      },
    });
  }, [history, newKey, location.search]);

  const columns = [
    {
      title: t("labels.audience"),
      dataIndex: "title",
      key: "column-title",
      className: "column-title",
      sorter: (a, b) =>
        a?.title.toString().localeCompare(b?.title.toString()),
      render: (text, record) => {
        return record?.title;
      },
    },
    {
      title: t("common.createdBy"),
      dataIndex: "createdByName",
      key: "column-createdByName",
      className: "column-createdByName",
      render: (text, record, index) => record?.createdByName,
    },
    {
      title: t("common.recipients"),
      dataIndex: "amount",
      key: "column-amount",
      className: "column-amount",
      render: (text, record, index) => record?.amount,
    },
  ];

  const onRowHandle = useCallback((record, index) => {
    return {
      onClick: () => history.push(`/audiences/${record?._id}`),
    };
  }, []);

  const onPaginateHandle = useCallback(pagination => {
    const { current, ...restPagination } = pagination;
    changeFilterParams({ page: current });
  }, []);

  // render function
  return (
    <Layout className="main-full-content">
      <Helmet>
        <title>{t("labels.audiences")}</title>
        <meta name="description" content="Description of Audiences" />
      </Helmet>
      <Content className="main-content-container">
        <div className="container-wrapped">
          <CustomPageHeader
            className="audiences-header"
            title={t("audiences.listOfAudiences")}
            subTitle={t("labels.audiences")}
            subTitlePosition="top"
            extraActions={[
              <Button
                className="ant-btn-submit"
                type="default"
                onClick={addNewItem}
              >
                {t("audiences.createAudience")}
              </Button>,
            ]}
          />

          <CustomTable
            className="audiences-table"
            dataSource={tableRows || []}
            columns={columns}
            onRow={onRowHandle}
            onPaginate={onPaginateHandle}
            loading={loading}
            pagination={{
              current: currentParams?.page,
              defaultCurrent: currentParams?.page || 1,
              position: ["bottomRight"],
              pageSize: limit || 10,
              total: totalAmount,
              defaultPageSize: totalPages,
              hideOnSinglePage: true,
              showSizeChanger: false,
            }}
          />
        </div>
      </Content>
    </Layout>
  );
}

Audiences.propTypes = {
  dispatch: PropTypes.func.isRequired,
  flushState: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  loadList: PropTypes.func.isRequired,
};

const mapStateToProps = createStructuredSelector({
  // list
  firstRender: makeSelectFirstRender(),
  itemsList: makeSelectItemsList(),
  loading: selectAudiencesLoading(),
  error: selectAudiencesError(),
  params: makeSelectItemsListFilteringParams(),
  totalAmount: makeSelectItemsListTotalAmount(),
  totalPages: makeSelectItemsTotalPages(),
  limit: makeSelectItemsLimitPerPage(),
});

function mapDispatchToProps(dispatch) {
  return {
    flushState: () => dispatch(flushStateAudiences()),
    loadList: (params, firstRenderValue) =>
      dispatch(loadAudiences(params, firstRenderValue)),
    dispatch,
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(
  withConnect,
  memo,
)(Audiences);
